/**
 * Task 167 — public-chat session persistence (sessionStorage).
 *
 * Serializes the visible thread so a page reload in the SAME tab restores the
 * completed conversation. CTO decision: `sessionStorage` (survives reload, clears
 * when the tab/session ends), never `localStorage`; no server retention; no query
 * text in the URL. Frontend-only, reducer-state serialization (NOT prop mirroring).
 *
 * What is persisted: completed turns only — each user question paired with a
 * SETTLED assistant answer (`complete` or `stopped`), including the answer text and
 * its source/model/cache metadata as already present in state. What is NOT
 * persisted: in-flight/streaming turns, errored turns, provider keys/bearer tokens
 * (never in client state to begin with), or any hidden prompt/system detail.
 *
 * The pure `serializeThread` / `reviveThread` functions hold the filtering + shape
 * validation and are unit-tested by `session-persistence.smoke.ts` with a stub
 * store, so the rules are verified without a real browser.
 */
import type { AssistantStatus, ChatMessage } from "./chat-reducer";
import type { AnswerResponse } from "../api/answer-client";
import type { Language } from "../strings";

const STORAGE_KEY = "bow-and-arrow:chat-session";
const SCHEMA_VERSION = 1;

/** Statuses durable enough to restore as history. Streaming/error are excluded. */
const PERSISTABLE_STATUSES: ReadonlySet<AssistantStatus> = new Set<AssistantStatus>([
  "complete",
  "stopped",
]);

interface PersistedSession {
  v: number;
  language: Language;
  thread: ChatMessage[];
}

function getStore(): Storage | null {
  try {
    if (typeof window === "undefined") return null;
    return window.sessionStorage ?? null;
  } catch {
    // Accessing sessionStorage can throw (privacy mode / disabled storage).
    return null;
  }
}

/**
 * Reduces a live thread to the durable history: each user turn paired with a
 * SETTLED assistant answer. Drops a trailing in-flight (user-only or streaming)
 * turn and any errored turn, so a reload never resurrects a partial answer as if
 * it were finished.
 */
export function serializeThread(thread: ChatMessage[]): ChatMessage[] {
  const out: ChatMessage[] = [];
  for (let i = 0; i < thread.length; i += 1) {
    const message = thread[i];
    if (message === undefined || message.role !== "user") continue;
    const next = thread[i + 1];
    if (
      next !== undefined &&
      next.role === "assistant" &&
      PERSISTABLE_STATUSES.has(next.status)
    ) {
      out.push(message, next);
      i += 1; // consumed the paired assistant turn
    }
  }
  return out;
}

/**
 * Cheap identity for the durable portion of a live thread. Streaming deltas do
 * not change it, while every newly completed/stopped pair does. useChat uses
 * this to avoid JSON.stringify + sessionStorage.setItem on every provider token.
 */
export function sessionRevision(thread: ChatMessage[]): string {
  const settled: string[] = [];
  for (let i = 0; i < thread.length; i += 1) {
    const message = thread[i];
    if (message === undefined || message.role !== "user") continue;
    const next = thread[i + 1];
    if (
      next !== undefined &&
      next.role === "assistant" &&
      PERSISTABLE_STATUSES.has(next.status)
    ) {
      settled.push(`${message.id}\u0000${next.id}\u0000${next.status}`);
      i += 1;
    }
  }
  return settled.join("\u0001");
}

/** Validates + narrows a stored answer payload, or null if it is unusable. A
 *  `complete` turn with a malformed response degrades to "no meta/sources" rather
 *  than crashing the restore. */
function reviveResponse(raw: unknown): AnswerResponse | null {
  if (typeof raw !== "object" || raw === null) return null;
  const o = raw as Record<string, unknown>;
  const model = o.model as { name?: unknown } | undefined;
  if (typeof o.answer !== "string") return null;
  if (!model || typeof model.name !== "string") return null;
  if (!Array.isArray(o.sources)) return null;
  return raw as AnswerResponse;
}

function reviveMessage(raw: unknown): ChatMessage | null {
  if (typeof raw !== "object" || raw === null) return null;
  const o = raw as Record<string, unknown>;
  if (typeof o.id !== "string" || typeof o.text !== "string") return null;

  if (o.role === "user") {
    return { id: o.id, role: "user", text: o.text };
  }
  if (o.role === "assistant") {
    if (o.status !== "complete" && o.status !== "stopped") return null;
    return {
      id: o.id,
      role: "assistant",
      text: o.text,
      status: o.status,
      response: reviveResponse(o.response),
      error: null,
      ...(typeof o.createdAt === "number" ? { createdAt: o.createdAt } : {}),
    };
  }
  return null;
}

/** Rebuilds a clean, well-formed thread from stored data, re-running the pairing
 *  filter so a tampered store cannot inject an orphan or non-settled turn. */
export function reviveThread(raw: unknown): ChatMessage[] {
  if (!Array.isArray(raw)) return [];
  const revived = raw.map(reviveMessage).filter((m): m is ChatMessage => m !== null);
  return serializeThread(revived);
}

/** Persists the durable history. An empty history REMOVES the key (e.g. after
 *  "Nueva conversación"). Best-effort: quota/serialization failures are swallowed. */
export function saveSession(thread: ChatMessage[], language: Language): void {
  const store = getStore();
  if (store === null) return;
  try {
    const persistable = serializeThread(thread);
    if (persistable.length === 0) {
      store.removeItem(STORAGE_KEY);
      return;
    }
    const payload: PersistedSession = { v: SCHEMA_VERSION, language, thread: persistable };
    store.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Non-fatal — persistence is a convenience, never a correctness dependency.
  }
}

/** Loads the restorable session, or null when there is nothing valid to restore
 *  (absent, wrong schema version, unparseable, or empty after revival). */
export function loadSession(): { thread: ChatMessage[]; language: Language } | null {
  const store = getStore();
  if (store === null) return null;
  try {
    const raw = store.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PersistedSession>;
    if (parsed?.v !== SCHEMA_VERSION) return null;
    const thread = reviveThread(parsed.thread);
    if (thread.length === 0) return null;
    const language: Language = parsed.language === "en" ? "en" : "es";
    return { thread, language };
  } catch {
    return null;
  }
}

export function clearSession(): void {
  const store = getStore();
  if (store === null) return;
  try {
    store.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}
